import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { coursesService, lessonsService, usersService, enrollmentsService, groupsService, evaluationsService } from '../config/supabase';
import Header from '../components/Header';
import LessonEditor from '../components/LessonEditor';
import '../styles/AdminPage.css';

const LOCK_MARKER_REGEX = /<!--\s*lock_seconds:(\d+)\s*-->/i;

const parseLessonContentMeta = (html) => {
  const raw = html || '';
  const match = raw.match(LOCK_MARKER_REGEX);
  const lockSeconds = match ? Math.max(0, parseInt(match[1], 10) || 0) : 120;
  const cleanContent = raw.replace(LOCK_MARKER_REGEX, '').trim();
  return { lockSeconds, cleanContent };
};

const buildLessonContentWithMeta = (html, lockSeconds) => {
  const seconds = Math.max(0, parseInt(lockSeconds, 10) || 0);
  const clean = (html || '').replace(LOCK_MARKER_REGEX, '').trim();
  return '<!--lock_seconds:' + seconds + '-->' + clean;
};

const createEmptyQuestion = () => ({
  prompt: '',
  options: [
    { option_text: '', is_correct: true },
    { option_text: '', is_correct: false },
    { option_text: '', is_correct: false },
    { option_text: '', is_correct: false },
  ],
});

const AdminPage = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    studentIds: [],
    courseIds: [],
  });
  const [bulkCourseId, setBulkCourseId] = useState('');
  const [bulkUsersRaw, setBulkUsersRaw] = useState('');
  const [bulkResult, setBulkResult] = useState('');
  const [lessons, setLessons] = useState([]);
  const [evaluationForm, setEvaluationForm] = useState({
    title: 'Evaluacion final',
    description: '',
    passingScore: 70,
    questions: [createEmptyQuestion()],
  });
  const [evaluationExists, setEvaluationExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Para gestión de accesos
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEnrollments, setUserEnrollments] = useState([]);

  // Curso seleccionado para editar/ver lecciones
  const [selectedCourse, setSelectedCourse] = useState(null);
  // Vista: 'list' | 'course-editor' | 'lesson-editor'
  const [view, setView] = useState('list');

  // Formulario de cursos
  const [courseForm, setCourseForm] = useState({
    titulo: '', descripcion: '', imagen: '', duracion: 0,
  });

  // Lección seleccionada para editar
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    titulo: '', descripcion: '', contenido: '', orden: 1,
  });
  const [lessonLockSeconds, setLessonLockSeconds] = useState(120);
  const [saveStatus, setSaveStatus] = useState(''); // 'success', 'error', o vacio

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await coursesService.getAll();
      setCourses(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLessons = useCallback(async (courseId) => {
    try {
      const data = await lessonsService.getByCourse(courseId);
      setLessons(data || []);
    } catch (error) {
      console.error('Error lecciones:', error);
      setLessons([]);
    }
  }, []);

  const loadEvaluation = useCallback(async (courseId) => {
    try {
      const data = await evaluationsService.getByCourse(courseId);
      if (!data) {
        setEvaluationExists(false);
        setEvaluationForm({
          title: 'Evaluacion final',
          description: '',
          passingScore: 70,
          questions: [createEmptyQuestion()],
        });
        return;
      }
      setEvaluationExists(true);
      setEvaluationForm({
        title: data.title || 'Evaluacion final',
        description: data.description || '',
        passingScore: data.passing_score || 70,
        questions: (data.questions || []).length > 0
          ? data.questions.map((question) => ({
              prompt: question.prompt || '',
              options: (question.options || []).map((option) => ({
                option_text: option.option_text || '',
                is_correct: !!option.is_correct,
              })),
            }))
          : [createEmptyQuestion()],
      });
    } catch (error) {
      console.error('Error evaluacion:', error);
      setEvaluationExists(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await usersService.getAll();
      setUsers(data || []);
    } catch (error) {
      console.error('Error usuarios:', error);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const data = await groupsService.getAll();
      setGroups(data || []);
    } catch (error) {
      console.error('Error grupos:', error);
      setGroups([]);
    }
  }, []);

  const loadUserEnrollments = async (userId) => {
    try {
      const data = await enrollmentsService.getByCourse('all').catch(() => []);
      // Mejor: obtener cursos a los que tiene acceso el usuario via query directa
      const { supabase } = await import('../config/supabase');
      const { data: enrData } = await supabase
        .from('enrollments')
        .select('course_id')
        .eq('user_id', userId);
      setUserEnrollments((enrData || []).map((e) => e.course_id));
    } catch (error) {
      setUserEnrollments([]);
    }
  };

  const handleSelectUser = async (u) => {
    setSelectedUser(u);
    // Cargar inscripciones del usuario (deduplicadas)
    try {
      const { supabase } = await import('../config/supabase');
      const { data } = await supabase
        .from('enrollments')
        .select('course_id')
        .eq('user_id', u.id);
      // Deduplicar en caso de duplicados en BD
      const uniqueIds = [...new Set((data || []).map((e) => e.course_id))];
      setUserEnrollments(uniqueIds);
    } catch {
      setUserEnrollments([]);
    }
  };

  const handleToggleEnrollment = async (courseId) => {
    if (!selectedUser) return;
    const isEnrolled = userEnrollments.includes(courseId);
    try {
      if (isEnrolled) {
        await enrollmentsService.unenroll(selectedUser.id, courseId);
        setUserEnrollments((prev) => prev.filter((id) => id !== courseId));
      } else {
        await enrollmentsService.enroll(selectedUser.id, courseId);
        setUserEnrollments((prev) => [...prev, courseId]);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    loadCourses();
  }, [isAdmin, navigate, loadCourses]);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'groups') loadUsers();
  }, [activeTab, loadUsers]);

  useEffect(() => {
    if (activeTab === 'groups') loadGroups();
  }, [activeTab, loadGroups]);

  // ─── CURSOS ───────────────────────────────────────────
  const openNewCourse = () => {
    setCourseForm({ titulo: '', descripcion: '', imagen: '', duracion: 0 });
    setSelectedCourse(null);
    setLessons([]);
    setView('course-editor');
  };

  const openEditCourse = async (course) => {
    setCourseForm({ titulo: course.titulo, descripcion: course.descripcion || '', imagen: course.imagen || '', duracion: course.duracion || 0 });
    setSelectedCourse(course);
    await loadLessons(course.id);
    await loadEvaluation(course.id);
    setView('course-editor');
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedCourse?.id) {
        await coursesService.update(selectedCourse.id, courseForm);
        setSelectedCourse({ ...selectedCourse, ...courseForm });
      } else {
        const created = await coursesService.create({ ...courseForm, created_by: user?.id });
        setSelectedCourse(created);
        await loadLessons(created.id);
      }
      await loadCourses();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('¿Borrar este curso y todas sus lecciones?')) return;
    try {
      await coursesService.delete(courseId);
      setView('list');
      loadCourses();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  // ─── LECCIONES ────────────────────────────────────────
  const openNewLesson = () => {
    setLessonForm({ titulo: '', descripcion: '', contenido: '', orden: lessons.length + 1 });
    setLessonLockSeconds(120);
    setSelectedLesson(null);
    setView('lesson-editor');
  };

  const openEditLesson = (lesson) => {
    const parsed = parseLessonContentMeta(lesson.contenido || '');
    setLessonForm({ titulo: lesson.titulo, descripcion: lesson.descripcion || '', contenido: parsed.cleanContent, orden: lesson.orden || 1 });
    setLessonLockSeconds(parsed.lockSeconds);
    setSelectedLesson(lesson);
    setView('lesson-editor');
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    const lessonPayload = {
      ...lessonForm,
      contenido: buildLessonContentWithMeta(lessonForm.contenido, lessonLockSeconds),
    };
    console.log('═══ GUARDANDO LECCIÓN ═══');
    console.log('ID:', selectedLesson?.id || 'nueva');
    console.log('Titulo:', lessonPayload.titulo);
    console.log('Descripcion:', lessonPayload.descripcion);
    console.log('Lock seconds:', lessonLockSeconds);
    console.log('Contenido (primeros 200 chars):', lessonPayload.contenido?.substring(0, 200));
    console.log('Contenido (longitud total):', lessonPayload.contenido?.length);
    setSaving(true);
    setSaveStatus('');
    try {
      if (selectedLesson?.id) {
        console.log('→ Actualizando lección existente:', selectedLesson.id);
        const result = await lessonsService.update(selectedLesson.id, lessonPayload);
        console.log('→ Resultado UPDATE:', result);
      } else {
        console.log('→ Creando nueva lección');
        const result = await lessonsService.create({ ...lessonPayload, course_id: selectedCourse.id });
        console.log('→ Resultado CREATE:', result);
      }
      console.log('✓ Lección guardada exitosamente');
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
      await loadLessons(selectedCourse.id);
      setTimeout(() => setView('course-editor'), 800);
    } catch (error) {
      console.error('✗ Error al guardar lección:', error);
      setSaveStatus('error');
      alert('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('¿Borrar esta lección?')) return;
    try {
      await lessonsService.delete(lessonId);
      await loadLessons(selectedCourse.id);
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const openEvaluationEditor = async () => {
    if (!selectedCourse?.id) {
      alert('Selecciona un curso para configurar la evaluación.');
      return;
    }
    setActiveTab('courses');
    await loadEvaluation(selectedCourse.id);
    setView('evaluation-editor');
  };

  const updateEvaluationQuestion = (questionIndex, updates) => {
    setEvaluationForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, index) => (
        index === questionIndex ? { ...question, ...updates } : question
      )),
    }));
  };

  const updateEvaluationOption = (questionIndex, optionIndex, updates) => {
    setEvaluationForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, qIndex) => {
        if (qIndex !== questionIndex) return question;
        let nextOptions = question.options.map((option, oIndex) => (
          oIndex === optionIndex ? { ...option, ...updates } : option
        ));
        if (Object.prototype.hasOwnProperty.call(updates, 'is_correct') && updates.is_correct) {
          nextOptions = nextOptions.map((option, idx) => ({
            ...option,
            is_correct: idx === optionIndex,
          }));
        }
        return { ...question, options: nextOptions };
      }),
    }));
  };

  const addEvaluationQuestion = () => {
    setEvaluationForm((prev) => ({
      ...prev,
      questions: [...prev.questions, createEmptyQuestion()],
    }));
  };

  const removeEvaluationQuestion = (questionIndex) => {
    setEvaluationForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, index) => index !== questionIndex),
    }));
  };

  const handleSaveEvaluation = async (e) => {
    e.preventDefault();
    if (!selectedCourse?.id) return;

    const cleanQuestions = evaluationForm.questions
      .map((question) => ({
        prompt: question.prompt.trim(),
        options: question.options
          .map((option) => ({
            option_text: option.option_text.trim(),
            is_correct: !!option.is_correct,
          }))
          .filter((option) => option.option_text),
      }))
      .filter((question) => question.prompt && question.options.length >= 2 && question.options.some((option) => option.is_correct));

    if (cleanQuestions.length === 0) {
      alert('Agrega al menos una pregunta valida con minimo dos opciones y una correcta.');
      return;
    }

    setSaving(true);
    try {
      await evaluationsService.saveByCourse({
        courseId: selectedCourse.id,
        title: evaluationForm.title.trim() || 'Evaluacion final',
        description: evaluationForm.description.trim(),
        passingScore: Math.min(100, Math.max(0, parseInt(evaluationForm.passingScore, 10) || 0)),
        questions: cleanQuestions,
        userId: user?.id,
      });
      setEvaluationExists(true);
      await loadEvaluation(selectedCourse.id);
      setView('course-editor');
    } catch (error) {
      alert('Error guardando evaluacion: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvaluation = async () => {
    if (!selectedCourse?.id) return;
    if (!window.confirm('¿Eliminar la evaluacion de este curso?')) return;
    setSaving(true);
    try {
      await evaluationsService.deleteByCourse(selectedCourse.id);
      setEvaluationExists(false);
      await loadEvaluation(selectedCourse.id);
      setView('course-editor');
    } catch (error) {
      alert('Error eliminando evaluacion: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── CARGA MASIVA DE USUARIOS (crear cuentas + asignar curso opcional) ──────
  const handleBulkEnroll = async () => {
    const courseId = bulkCourseId.trim();
    const rows = bulkUsersRaw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.split(/[;,|]/).map((x) => x.trim()));

    if (rows.length === 0) {
      alert('Pega al menos una linea en formato: usuario,nombre completo,correo,contraseña');
      return;
    }

    setSaving(true);
    setBulkResult('');
    try {
      let created = 0;
      let assigned = 0;
      const failed = [];

      for (const parts of rows) {
        if (parts.length < 4) {
          failed.push(parts.join(',') + ' (formato invalido)');
          continue;
        }
        const username = parts[0];
        const fullName = parts[1];
        const email = String(parts[2] || '').toLowerCase();
        const password = parts[3];

        try {
          const result = await usersService.createWithPassword({ username, fullName, email, password });
          if (result?.user?.id) {
            created += 1;
            if (courseId) {
              try {
                await enrollmentsService.enroll(result.user.id, courseId);
                assigned += 1;
              } catch {
                // duplicate or policy errors are skipped
              }
            }
          } else {
            failed.push(email + ' (no se pudo crear)');
          }
        } catch (err) {
          failed.push(email + ' (' + (err?.message || 'error') + ')');
        }
      }

      const msg =
        'Creados: ' + created + '\n' +
        (courseId ? ('Asignados al curso: ' + assigned + '\n') : '') +
        'Fallidos: ' + failed.length +
        (failed.length ? '\n' + failed.join('\n') : '');
      setBulkResult(msg);
      await loadUsers();
      if (selectedUser?.id) {
        await loadUserEnrollments(selectedUser.id);
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── GRUPOS (Supabase) ─────────────────────────────────────────────────────
  const resetGroupForm = () => {
    setGroupForm({ name: '', description: '', studentIds: [], courseIds: [] });
    setSelectedGroupId(null);
  };

  const openGroup = (group) => {
    setSelectedGroupId(group.id);
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      studentIds: group.studentIds || [],
      courseIds: group.courseIds || [],
    });
  };

  const toggleGroupStudent = (userId) => {
    setGroupForm((prev) => ({
      ...prev,
      studentIds: prev.studentIds.includes(userId)
        ? prev.studentIds.filter((id) => id !== userId)
        : [...prev.studentIds, userId],
    }));
  };

  const toggleGroupCourse = (courseId) => {
    setGroupForm((prev) => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter((id) => id !== courseId)
        : [...prev.courseIds, courseId],
    }));
  };

  const saveGroup = async () => {
    const name = groupForm.name.trim();
    if (!name) {
      alert('El grupo necesita nombre');
      return;
    }
    setSaving(true);
    try {
      const saved = await groupsService.save({
        id: selectedGroupId,
        name,
        description: groupForm.description,
        created_by: user?.id,
      });
      await groupsService.setMembers(saved.id, groupForm.studentIds);
      await groupsService.setCourses(saved.id, groupForm.courseIds);
      await loadGroups();
      setSelectedGroupId(saved.id);
      alert('Grupo guardado correctamente');
    } catch (error) {
      alert('Error guardando grupo: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('¿Eliminar este grupo?')) return;
    try {
      await groupsService.delete(groupId);
      await loadGroups();
      if (selectedGroupId === groupId) resetGroupForm();
    } catch (error) {
      alert('Error eliminando grupo: ' + error.message);
    }
  };

  const applyGroupEnrollments = async () => {
    if (groupForm.studentIds.length === 0 || groupForm.courseIds.length === 0) {
      alert('Selecciona alumnos y cursos para aplicar el grupo');
      return;
    }
    setSaving(true);
    try {
      let assigned = 0;
      for (const userId of groupForm.studentIds) {
        for (const courseId of groupForm.courseIds) {
          try {
            await enrollmentsService.enroll(userId, courseId);
            assigned += 1;
          } catch {
            // duplicate enrollment or validation issues are skipped
          }
        }
      }
      alert('Grupo aplicado. Nuevas asignaciones: ' + assigned);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <Header />

      <div className="admin-layout">
        {/* ── SIDEBAR ── */}
        <aside className="admin-sidebar">
          <div className="sidebar-logo">⚙️ Admin</div>
          <nav className="sidebar-nav">
            <button
              className={`sidebar-item ${activeTab === 'courses' ? 'active' : ''}`}
              onClick={() => { setActiveTab('courses'); setView('list'); }}
            >
              <span className="sidebar-icon">📚</span> Cursos
            </button>
            <button
              className={`sidebar-item ${view === 'evaluation-editor' ? 'active' : ''}`}
              onClick={() => {
                if (!selectedCourse) {
                  setActiveTab('courses');
                  setView('list');
                  alert('Selecciona un curso para configurar la evaluación.');
                  return;
                }
                openEvaluationEditor();
              }}
            >
              <span className="sidebar-icon">📝</span> Evaluación
            </button>
            <button
              className={`sidebar-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <span className="sidebar-icon">👥</span> Usuarios
            </button>
            <button
              className={`sidebar-item ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              <span className="sidebar-icon">🧩</span> Grupos
            </button>
            <button
              className={`sidebar-item ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              <span className="sidebar-icon">📈</span> Estadísticas
            </button>
          </nav>
          <div className="sidebar-footer">
            <button className="sidebar-back" onClick={() => navigate('/dashboard')}>
              ← Volver al inicio
            </button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main className="admin-main">

          {/* ═══════════ CURSOS: LISTA ═══════════ */}
          {activeTab === 'courses' && view === 'list' && (
            <div className="content-area">
              <div className="content-header">
                <div>
                  <h1>Mis Cursos</h1>
                  <p className="subtitle">{courses.length} cursos publicados</p>
                </div>
                <button className="btn-create" onClick={openNewCourse}>
                  ➕ Nuevo Curso
                </button>
              </div>

              {loading ? (
                <div className="loading-state"><div className="spinner" />Cargando...</div>
              ) : courses.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📚</div>
                  <h3>No hay cursos aún</h3>
                  <p>Crea tu primer curso para empezar</p>
                  <button className="btn-create" onClick={openNewCourse}>➕ Crear Curso</button>
                </div>
              ) : (
                <div className="courses-grid">
                  {courses.map((course) => (
                    <div key={course.id} className="course-card" onClick={() => openEditCourse(course)}>
                      <div className="course-card-img">
                        {course.imagen
                          ? <img src={course.imagen} alt={course.titulo} onError={(e) => { e.target.style.display='none'; }} />
                          : <div className="course-card-placeholder">📚</div>}
                      </div>
                      <div className="course-card-body">
                        <h3>{course.titulo}</h3>
                        <p>{course.descripcion?.substring(0, 80)}{course.descripcion?.length > 80 ? '…' : ''}</p>
                        <div className="course-card-meta">
                          <span>⏱️ {course.duracion}h</span>
                        </div>
                      </div>
                      <div className="course-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="btn-icon btn-edit-icon" onClick={() => openEditCourse(course)} title="Editar">✏️</button>
                        <button className="btn-icon btn-delete-icon" onClick={() => handleDeleteCourse(course.id)} title="Eliminar">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════ CURSOS: EDITOR ═══════════ */}
          {activeTab === 'courses' && view === 'course-editor' && (
            <div className="editor-layout">
              {/* Panel izquierdo: formulario del curso */}
              <div className="editor-left">
                <div className="editor-breadcrumb">
                  <button className="breadcrumb-back" onClick={() => setView('list')}>← Cursos</button>
                  <span>/</span>
                  <span>{selectedCourse ? selectedCourse.titulo : 'Nuevo Curso'}</span>
                </div>

                <div className="editor-card">
                  <h2>{selectedCourse ? 'Editar Curso' : 'Nuevo Curso'}</h2>
                  <form onSubmit={handleSaveCourse}>
                    <div className="field">
                      <label>Título del curso *</label>
                      <input
                        type="text"
                        placeholder="Ej: React desde Cero"
                        value={courseForm.titulo}
                        onChange={(e) => setCourseForm({ ...courseForm, titulo: e.target.value })}
                        required
                      />
                    </div>

                    <div className="field">
                      <label>Descripción</label>
                      <textarea
                        placeholder="¿Qué aprenderán los alumnos?"
                        value={courseForm.descripcion}
                        onChange={(e) => setCourseForm({ ...courseForm, descripcion: e.target.value })}
                        rows={4}
                      />
                    </div>

                    <div className="field-row">
                      <div className="field">
                        <label>Duración (horas)</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="20"
                          value={courseForm.duracion}
                          onChange={(e) => setCourseForm({ ...courseForm, duracion: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>

                    <div className="field">
                      <label>URL de imagen</label>
                      <input
                        type="url"
                        placeholder="https://..."
                        value={courseForm.imagen}
                        onChange={(e) => setCourseForm({ ...courseForm, imagen: e.target.value })}
                      />
                      {courseForm.imagen && (
                        <img src={courseForm.imagen} alt="preview" className="img-preview" onError={(e) => { e.target.style.display='none'; }} />
                      )}
                    </div>

                    <div className="field-hint">
                      💡 Para videos en lecciones, pega el código embed de YouTube:
                      <code>&lt;iframe src="https://youtube.com/embed/ID"&gt;&lt;/iframe&gt;</code>
                    </div>

                    <button type="submit" className="btn-save-course" disabled={saving}>
                      {saving ? 'Guardando…' : selectedCourse ? '💾 Guardar Cambios' : '🚀 Crear Curso'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Panel derecho: lecciones */}
              <div className="editor-right">
                <div className="lessons-panel">
                  <div className="lessons-panel-header">
                    <h3>📝 Lecciones <span className="badge">{lessons.length}</span></h3>
                    <button
                      className="btn-add-lesson"
                      onClick={openNewLesson}
                      disabled={!selectedCourse}
                      title={!selectedCourse ? 'Guarda el curso primero' : ''}
                    >
                      ➕ Agregar
                    </button>
                  </div>

                  {!selectedCourse && (
                    <div className="lessons-hint">Guarda el curso primero para agregar lecciones</div>
                  )}

                  {selectedCourse && lessons.length === 0 && (
                    <div className="lessons-empty">
                      <span>📭</span>
                      <p>Sin lecciones aún</p>
                      <button className="btn-add-lesson-empty" onClick={openNewLesson}>Agregar primera lección</button>
                    </div>
                  )}

                  <div className="lessons-list">
                    {lessons.map((lesson, idx) => (
                      <div key={lesson.id} className="lesson-row">
                        <div className="lesson-num">{idx + 1}</div>
                        <div className="lesson-info">
                          <span className="lesson-title">{lesson.titulo}</span>
                          <span className="lesson-desc">{lesson.descripcion}</span>
                        </div>
                        <div className="lesson-row-actions">
                          <button className="btn-icon" onClick={() => openEditLesson(lesson)} title="Editar">✏️</button>
                          <button className="btn-icon btn-delete-icon" onClick={() => handleDeleteLesson(lesson.id)} title="Eliminar">🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="evaluation-panel-box">
                    <div className="evaluation-panel-head">
                      <div>
                        <strong>Evaluacion final</strong>
                        <span>{evaluationExists ? 'Configurada' : 'Sin configurar'}</span>
                      </div>
                      <button
                        className="btn-add-lesson"
                        onClick={openEvaluationEditor}
                        disabled={!selectedCourse}
                      >
                        {evaluationExists ? 'Editar' : 'Crear'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ LECCIÓN: EDITOR ═══════════ */}
          {activeTab === 'courses' && view === 'lesson-editor' && (
            <div className="content-area">
              <div className="editor-breadcrumb">
                <button className="breadcrumb-back" onClick={() => setView('course-editor')}>← {selectedCourse?.titulo}</button>
                <span>/</span>
                <span>{selectedLesson ? selectedLesson.titulo : 'Nueva Lección'}</span>
              </div>

              <div className="lesson-editor-card">
                <h2>{selectedLesson ? 'Editar Lección' : 'Nueva Lección'}</h2>
                <form onSubmit={handleSaveLesson}>
                  <div className="field-row">
                    <div className="field" style={{flex: 3}}>
                      <label>Título de la lección *</label>
                      <input
                        type="text"
                        placeholder="Ej: Introducción a React Hooks"
                        value={lessonForm.titulo}
                        onChange={(e) => setLessonForm({ ...lessonForm, titulo: e.target.value })}
                        required
                      />
                    </div>
                    <div className="field" style={{flex: 1}}>
                      <label>Orden</label>
                      <input
                        type="number"
                        min="1"
                        value={lessonForm.orden}
                        onChange={(e) => setLessonForm({ ...lessonForm, orden: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                    <div className="field" style={{flex: 1}}>
                      <label>Bloqueo (segundos)</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={lessonLockSeconds}
                        onChange={(e) => setLessonLockSeconds(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label>Descripción breve</label>
                    <input
                      type="text"
                      placeholder="Resumen de esta lección"
                      value={lessonForm.descripcion}
                      onChange={(e) => setLessonForm({ ...lessonForm, descripcion: e.target.value })}
                    />
                  </div>

                  <div className="field">
                    <label>Contenido de la lección</label>
                    <LessonEditor
                      key={selectedLesson?.id || 'new-lesson-editor'}
                      value={lessonForm.contenido}
                      onChange={(html) => setLessonForm((f) => ({ ...f, contenido: html }))}
                    />
                  </div>

                  <div className="lesson-form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setView('course-editor')}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn-save-course" disabled={saving}>
                      {saving ? 'Guardando…' : selectedLesson ? 'Guardar Cambios' : 'Agregar Leccion'}
                    </button>
                    {saveStatus === 'success' && <span className="save-success">Guardado!</span>}
                    {saveStatus === 'error' && <span className="save-error">Error</span>}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ═══════════ EVALUACION: EDITOR ═══════════ */}
          {activeTab === 'courses' && view === 'evaluation-editor' && (
            <div className="content-area">
              <div className="editor-breadcrumb">
                <button className="breadcrumb-back" onClick={() => setView('course-editor')}>← {selectedCourse?.titulo}</button>
                <span>/</span>
                <span>Evaluacion final</span>
              </div>

              <div className="lesson-editor-card">
                <h2>Evaluacion del curso</h2>
                <form onSubmit={handleSaveEvaluation}>
                  <div className="field-row">
                    <div className="field" style={{ flex: 2 }}>
                      <label>Titulo</label>
                      <input
                        type="text"
                        value={evaluationForm.title}
                        onChange={(e) => setEvaluationForm((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="Evaluacion final"
                      />
                    </div>
                    <div className="field" style={{ flex: 1 }}>
                      <label>Puntaje minimo para aprobar (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={evaluationForm.passingScore}
                        onChange={(e) => setEvaluationForm((prev) => ({ ...prev, passingScore: parseInt(e.target.value, 10) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label>Descripcion</label>
                    <textarea
                      rows={3}
                      value={evaluationForm.description}
                      onChange={(e) => setEvaluationForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Indicaciones para el alumno"
                    />
                  </div>

                  <div className="evaluation-editor-list">
                    {evaluationForm.questions.map((question, questionIndex) => (
                      <div key={'question-' + questionIndex} className="evaluation-question-card">
                        <div className="evaluation-question-head">
                          <h3>Pregunta {questionIndex + 1}</h3>
                          {evaluationForm.questions.length > 1 && (
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => removeEvaluationQuestion(questionIndex)}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>

                        <div className="field">
                          <label>Pregunta</label>
                          <input
                            type="text"
                            value={question.prompt}
                            onChange={(e) => updateEvaluationQuestion(questionIndex, { prompt: e.target.value })}
                            placeholder="Escribe la pregunta"
                          />
                        </div>

                        <div className="evaluation-options-grid">
                          {question.options.map((option, optionIndex) => (
                            <div key={'option-' + questionIndex + '-' + optionIndex} className="evaluation-option-row">
                              <input
                                type="radio"
                                name={'correct-option-' + questionIndex}
                                checked={!!option.is_correct}
                                onChange={() => updateEvaluationOption(questionIndex, optionIndex, { is_correct: true })}
                              />
                              <input
                                type="text"
                                value={option.option_text}
                                onChange={(e) => updateEvaluationOption(questionIndex, optionIndex, { option_text: e.target.value })}
                                placeholder={'Opcion ' + (optionIndex + 1)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="evaluation-toolbar">
                    <button type="button" className="btn-add-lesson" onClick={addEvaluationQuestion}>
                      + Agregar pregunta
                    </button>
                  </div>

                  <div className="lesson-form-actions">
                    {evaluationExists && (
                      <button type="button" className="btn-cancel" onClick={handleDeleteEvaluation}>
                        Eliminar evaluacion
                      </button>
                    )}
                    <button type="button" className="btn-cancel" onClick={() => setView('course-editor')}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn-save-course" disabled={saving}>
                      {saving ? 'Guardando…' : 'Guardar evaluacion'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ═══════════ USUARIOS ═══════════ */}
          {activeTab === 'users' && (
            <div className="content-area">
              <div className="content-header">
                <div>
                  <h1>Usuarios</h1>
                  <p className="subtitle">{users.length} usuarios · Gestiona acceso a cursos</p>
                </div>
              </div>

              <div className="bulk-card">
                <div className="bulk-head">
                  <h3>Carga masiva de usuarios</h3>
                  <span>Formato por linea: usuario,nombre completo,correo,contraseña</span>
                </div>
                <div className="bulk-grid">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Curso destino (opcional)</label>
                    <select
                      className="bulk-select"
                      value={bulkCourseId}
                      onChange={(e) => setBulkCourseId(e.target.value)}
                    >
                      <option value="">Selecciona un curso</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>{c.titulo}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Usuarios a crear</label>
                    <textarea
                      className="bulk-textarea"
                      rows={4}
                      placeholder={'usuario1,Nombre Uno,uno@correo.com,Password123\nusuario2,Nombre Dos,dos@correo.com,Password456'}
                      value={bulkUsersRaw}
                      onChange={(e) => setBulkUsersRaw(e.target.value)}
                    />
                  </div>
                </div>

                <div className="bulk-actions">
                  <button className="btn-create" onClick={handleBulkEnroll} disabled={saving}>
                    {saving ? 'Procesando...' : 'Crear usuarios masivamente'}
                  </button>
                  {bulkResult && <pre className="bulk-result">{bulkResult}</pre>}
                </div>
              </div>

              <div className="users-layout">
                {/* Lista de usuarios */}
                <div className="users-list-panel">
                  <div className="panel-title">Selecciona un usuario</div>
                  {users.length === 0 && <p className="panel-empty">No hay usuarios</p>}
                  {users.map((u) => (
                    <div
                      key={u.id}
                      className={`user-item ${selectedUser?.id === u.id ? 'active' : ''}`}
                      onClick={() => handleSelectUser(u)}
                    >
                      <div className="user-avatar">{(u.full_name || u.email || '?')[0].toUpperCase()}</div>
                      <div className="user-item-info">
                        <span className="user-item-name">{u.full_name || '—'}</span>
                        <span className="user-item-email">{u.email}</span>
                      </div>
                      <span className={`role-tag ${u.role}`}>{u.role === 'admin' ? '👑' : '👤'}</span>
                    </div>
                  ))}
                </div>

                {/* Panel de cursos del usuario */}
                <div className="enrollment-panel">
                  {!selectedUser ? (
                    <div className="enrollment-empty">
                      <span>👈</span>
                      <p>Selecciona un usuario para gestionar sus cursos</p>
                    </div>
                  ) : (
                    <>
                      <div className="enrollment-header">
                        <h3>Cursos de <strong>{selectedUser.full_name || selectedUser.email}</strong></h3>
                        <p className="subtitle">{userEnrollments.length} cursos asignados</p>
                      </div>
                      <div className="enrollment-courses">
                        {courses.length === 0 && <p className="panel-empty">No hay cursos creados aún</p>}
                        {courses.map((course) => {
                          const enrolled = userEnrollments.includes(course.id);
                          return (
                            <div key={course.id} className={`enrollment-row ${enrolled ? 'enrolled' : ''}`}>
                              <div className="enrollment-info">
                                <span className="enrollment-title">{course.titulo}</span>
                                <span className="enrollment-sub">⏱️ {course.duracion}h</span>
                              </div>
                              <button
                                className={`btn-toggle ${enrolled ? 'btn-unenroll' : 'btn-enroll'}`}
                                onClick={() => handleToggleEnrollment(course.id)}
                              >
                                {enrolled ? '✕ Quitar' : '＋ Dar acceso'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ GRUPOS ═══════════ */}
          {activeTab === 'groups' && (
            <div className="content-area">
              <div className="content-header">
                <div>
                  <h1>Grupos</h1>
                  <p className="subtitle">Agrupa alumnos y asigna cursos por lote</p>
                </div>
              </div>

              <div className="groups-layout">
                <div className="groups-list-card">
                  <div className="panel-title">Grupos creados ({groups.length})</div>
                  <div className="groups-list">
                    {groups.length === 0 && <p className="panel-empty">No hay grupos creados</p>}
                    {groups.map((g) => (
                      <div
                        key={g.id}
                        className={`group-item ${selectedGroupId === g.id ? 'active' : ''}`}
                        onClick={() => openGroup(g)}
                      >
                        <div className="group-item-main">
                          <strong>{g.name}</strong>
                          <span>{(g.studentIds || []).length} alumnos · {(g.courseIds || []).length} cursos</span>
                        </div>
                        <button
                          className="btn-icon btn-delete-icon"
                          title="Eliminar grupo"
                          onClick={(e) => { e.stopPropagation(); deleteGroup(g.id); }}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="group-editor-card">
                  <div className="group-editor-head">
                    <h3>{selectedGroupId ? 'Editar grupo' : 'Nuevo grupo'}</h3>
                    <div className="group-editor-actions">
                      <button className="btn-cancel" onClick={resetGroupForm}>Limpiar</button>
                      <button className="btn-create" onClick={saveGroup}>Guardar grupo</button>
                      <button className="btn-save-course" onClick={applyGroupEnrollments} disabled={saving}>
                        {saving ? 'Aplicando...' : 'Aplicar grupo (dar accesos)'}
                      </button>
                    </div>
                  </div>

                  <div className="field-row">
                    <div className="field" style={{ flex: 1 }}>
                      <label>Nombre del grupo *</label>
                      <input
                        type="text"
                        value={groupForm.name}
                        placeholder="Ej: Generación Mayo"
                        onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    </div>
                    <div className="field" style={{ flex: 2 }}>
                      <label>Descripción</label>
                      <input
                        type="text"
                        value={groupForm.description}
                        placeholder="Descripción opcional"
                        onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="group-pickers">
                    <div className="picker-card">
                      <div className="panel-title">Alumnos del grupo</div>
                      <div className="picker-list">
                        {users.length === 0 && <p className="panel-empty">No hay usuarios</p>}
                        {users.map((u) => {
                          const checked = groupForm.studentIds.includes(u.id);
                          return (
                            <label key={u.id} className="picker-item">
                              <input type="checkbox" checked={checked} onChange={() => toggleGroupStudent(u.id)} />
                              <span>{u.full_name || u.email}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="picker-card">
                      <div className="panel-title">Cursos del grupo</div>
                      <div className="picker-list">
                        {courses.length === 0 && <p className="panel-empty">No hay cursos</p>}
                        {courses.map((c) => {
                          const checked = groupForm.courseIds.includes(c.id);
                          return (
                            <label key={c.id} className="picker-item">
                              <input type="checkbox" checked={checked} onChange={() => toggleGroupCourse(c.id)} />
                              <span>{c.titulo}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ ESTADÍSTICAS ═══════════ */}
          {activeTab === 'stats' && (
            <div className="content-area">
              <div className="content-header">
                <h1>Estadísticas</h1>
              </div>
              <div className="stats-grid">
                <div className="stat-card"><div className="stat-icon">📚</div><div className="stat-number">{courses.length}</div><div className="stat-label">Cursos</div></div>
                <div className="stat-card"><div className="stat-icon">👥</div><div className="stat-number">{users.length}</div><div className="stat-label">Usuarios</div></div>
                <div className="stat-card"><div className="stat-icon">⏱️</div><div className="stat-number">{courses.reduce((s, c) => s + (c.duracion || 0), 0)}h</div><div className="stat-label">Horas de contenido</div></div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminPage;
